// GRWM's stills stage (spec G14-grwm, PR D; a screen with no live equivalent, GRWM-13, built on
// the staged kit as Pixar Talking Ad's `PixarCast.tsx`): "Cast & set", then the sixteen stills
// in the playbook's order, each 9:16 with its ↻, which redraws it and every still drawn from it.
import { Button, Group, Stack, Text } from "@mantine/core";
import { useState } from "react";

import type { GrwmDraftStill, GrwmStillName } from "../../../api/generated/model";
import { MediaPreview, type PreviewItem } from "../../../platform/media/MediaPreview";
import { ConfirmModal } from "../../../ui/ConfirmModal";
import { StillCard } from "./StillCard";
import { stillWaiting } from "./grwm-draft";
import classes from "./podcast.module.css";

/** Our labels, a still's beat or what it locks (`backend/data/templates/grwm/shots.yaml`). */
export const STILL_LABELS: Readonly<Record<GrwmStillName, string>> = {
  img1: "Her face",
  img2c: "Concealer",
  img3c: "Lipstick",
  img2: "Blush",
  img3: "Curls",
  dress: "The dress",
  body: "Full body",
  wear_front: "Dress, front",
  wear_side: "Dress, side",
  img4: "Mirror check",
  img5: "Entryway",
  door: "Front door",
  img6s: "Selfie",
  rest_plate: "The restaurant",
  img7r: "At the table",
  img8r: "The toast",
};

/** A made still in the shared lightbox, named by its label. */
function stillItem(still: GrwmDraftStill & { url: string }): PreviewItem {
  return {
    id: still.name,
    name: STILL_LABELS[still.name],
    kind: "image",
    url: still.url,
    thumb: still.url,
    poster: null,
    durationMs: null,
    width: null,
    height: null,
  };
}

export interface GrwmStillsStageProps {
  /** The draft's sixteen stills in the playbook's order (the server's). */
  stills: readonly GrwmDraftStill[];
  busy: boolean;
  resetting: boolean;
  onRegenerate: (name: GrwmStillName) => void;
  onStartOver: () => void;
}

/**
 * The stills stage: "Cast & set" and a row of the draft's stills, each with its label under it;
 * a done still opens in the shared lightbox, stepping through the made ones; "Start over" clears
 * the draft, as the other staged forms'.
 */
export function GrwmStillsStage(props: GrwmStillsStageProps) {
  const { stills, busy, resetting, onRegenerate, onStartOver } = props;
  const [preview, setPreview] = useState<GrwmStillName | null>(null);
  const [confirming, setConfirming] = useState(false);
  const made = stills.flatMap((still) => (still.url ? [{ ...still, url: still.url }] : []));
  return (
    <Stack gap={12} aria-label="Cast & set" component="section">
      <Group justify="space-between" wrap="nowrap">
        <Text className={classes.castTitle}>Cast &amp; set</Text>
        <Button variant="subtle" size="compact-sm" disabled={resetting || busy} onClick={() => setConfirming(true)}>
          Start over
        </Button>
      </Group>
      <div className={classes.stills}>
        {stills.map((still) => (
          <StillCard
            key={still.name}
            label={STILL_LABELS[still.name]}
            still={still}
            waiting={stillWaiting(still.name, stills)}
            busy={busy}
            onRegenerate={() => onRegenerate(still.name)}
            onFixLabel={null}
            onPreview={() => setPreview(still.name)}
          />
        ))}
      </div>
      <MediaPreview
        items={made.map(stillItem)}
        current={preview}
        onShow={(name) => setPreview(name as GrwmStillName | null)}
      />
      <ConfirmModal
        opened={confirming}
        title="Start over"
        confirmLabel="Start over"
        pending={resetting}
        onCancel={() => setConfirming(false)}
        onConfirm={() => {
          onStartOver();
          setConfirming(false);
        }}
      >
        <Text size="sm">The script and the sixteen images are cleared, and the form starts again from the approved locks.</Text>
      </ConfirmModal>
    </Stack>
  );
}
